import type { z } from "zod";
import { ApiError, errorEnvelopeSchema, isErrorCode } from "./errors";
import { HEADER_IDEMPOTENCY, HEADER_TENANT } from "./headers";

/**
 * Thin typed fetch client shared by the SPAs and scripts. Every response is validated with the
 * Zod schema the caller passes, so the UI and the API agree on one contract (CLAUDE.md "Validation").
 */
export interface ApiClientOptions {
  /** Origin of the API, without `/v1`. Empty string means same origin (Vite proxy locally). */
  baseUrl: string;
  getTenantId?: () => string | null | undefined;
  fetch?: typeof fetch;
  /** Extra headers on every request (scripts and tests: cookies, Access JWT). */
  headers?: () => Record<string, string>;
}

export interface RequestOptions<T> {
  body?: unknown;
  schema?: z.ZodType<T>;
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Sent as-is instead of JSON (photo uploads); `contentType` becomes the Content-Type. */
  rawBody?: Blob | ArrayBuffer;
  contentType?: string;
}

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

type WithoutSchema<T> = Omit<RequestOptions<T>, "schema">;
type WithoutSchemaBody<T> = Omit<RequestOptions<T>, "schema" | "body">;

export interface ApiClient {
  request<T = unknown>(method: HttpMethod, path: string, opts?: RequestOptions<T>): Promise<T>;
  get<T>(path: string, schema: z.ZodType<T>, opts?: WithoutSchema<T>): Promise<T>;
  post<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    opts?: WithoutSchemaBody<T>,
  ): Promise<T>;
  patch<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    opts?: WithoutSchemaBody<T>,
  ): Promise<T>;
  put<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    opts?: WithoutSchemaBody<T>,
  ): Promise<T>;
  delete<T>(path: string, schema: z.ZodType<T>, opts?: WithoutSchema<T>): Promise<T>;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? fetch;
  const base = options.baseUrl.replace(/\/$/, "");

  async function request<T>(
    method: HttpMethod,
    path: string,
    opts: RequestOptions<T> = {},
  ): Promise<T> {
    const relative = `${base}/v1${path.startsWith("/") ? path : `/${path}`}`;
    const url = new URL(relative, base || "http://localhost");
    if (opts.query) {
      for (const [k, v] of Object.entries(opts.query)) {
        if (v !== undefined) url.searchParams.set(k, String(v));
      }
    }
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(options.headers?.() ?? {}),
      ...(opts.headers ?? {}),
    };
    const tenantId = options.getTenantId?.();
    if (tenantId) headers[HEADER_TENANT] = tenantId;
    if (opts.idempotencyKey) headers[HEADER_IDEMPOTENCY] = opts.idempotencyKey;
    let body: string | Blob | ArrayBuffer | undefined;
    if (opts.rawBody !== undefined) {
      headers["Content-Type"] = opts.contentType ?? "application/octet-stream";
      body = opts.rawBody;
    } else if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(opts.body);
    }
    // Same-origin relative URLs keep the Vite proxy path; absolute origins go through as-is.
    const target = base ? url.toString() : `${url.pathname}${url.search}`;
    // `credentials` is a browser-only option; cast so the same file typechecks under Workers types.
    const init = {
      method,
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
    } as RequestInit;
    const res = await doFetch(target, init);
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : null;
    if (!res.ok) {
      const parsed = errorEnvelopeSchema.safeParse(json);
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        throw new ApiError(isErrorCode(code) ? code : "internal", message, details);
      }
      throw new ApiError("internal", `Request failed with status ${res.status}`);
    }
    if (!opts.schema) return json as T;
    const result = opts.schema.safeParse(json);
    if (!result.success) {
      throw new ApiError(
        "internal",
        "The server sent an unexpected response.",
        result.error.issues,
      );
    }
    return result.data;
  }

  return {
    request,
    get: (path, schema, opts) => request("GET", path, { ...opts, schema }),
    post: (path, body, schema, opts) => request("POST", path, { ...opts, body, schema }),
    patch: (path, body, schema, opts) => request("PATCH", path, { ...opts, body, schema }),
    put: (path, body, schema, opts) => request("PUT", path, { ...opts, body, schema }),
    delete: (path, schema, opts) => request("DELETE", path, { ...opts, schema }),
  };
}
